import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Check, ChevronLeft, Clock, X } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Progress,
  Skeleton,
  StatusBadge,
  SubBar,
} from '@/design/components';
import { errorMessage } from '@/lib/errors';
import {
  useQuiz,
  useQuizRun,
  type AnswerResult,
  type QuizQuestion,
  type QuizResult,
} from './api';

/**
 * מסך החידון (PRD 5.6.3).
 *
 * שאלה אחת על המסך, משוב מיד אחרי כל תשובה, ובסוף ציון. התשובה
 * נשלחת לשרת ברגע הלחיצה: אין "שמירה" בסוף שאפשר לאבד, ואי אפשר
 * לחזור ולשנות תשובה אחרי שראו אם היא נכונה.
 */
export function QuizPage() {
  const { quizId = '' } = useParams();
  const quiz = useQuiz(quizId);
  const { start, answer, finish } = useQuizRun(quizId);

  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<AnswerResult | null>(null);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // נקבע ב effect ולא ב render: Date.now בזמן רינדור אינו טהור, ושני
  // רינדורים של אותה שאלה היו מקבלים זמן התחלה שונה.
  const shownAt = useRef<number>(0);

  const questions = quiz.data?.questions ?? [];
  const current: QuizQuestion | null = questions[index] ?? null;
  const isLast = index >= questions.length - 1;

  // מודדים זמן לכל שאלה בנפרד, בשביל התחרויות (PRD 5.7)
  useEffect(() => {
    shownAt.current = Date.now();
  }, [index]);

  if (quiz.isPending) return <Skeleton className="m-4 h-64" />;
  if (quiz.isError) return <ErrorState onRetry={() => void quiz.refetch()} />;
  if (!quiz.data) return <Navigate to="/learning" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  const pick = (optionId: string) => {
    if (feedback || !attemptId || !current) return;
    setPicked(optionId);
    run(() =>
      answer
        .mutateAsync({
          attemptId,
          questionId: current.id,
          optionId,
          ms: Date.now() - shownAt.current,
        })
        .then(setFeedback),
    );
  };

  const advance = () => {
    if (!attemptId) return;
    if (isLast) {
      run(() => finish.mutateAsync(attemptId).then(setResult));
      return;
    }
    setFeedback(null);
    setPicked(null);
    setIndex(index + 1);
  };

  return (
    <div className="grid content-start gap-4">
      <SubBar title={quiz.data.title} subtitle="חידון" />

      <div className="grid gap-4 px-4 pb-8">
        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        {questions.length === 0 ? (
          <EmptyState title="החידון עוד ריק" description="עדיין לא נוספו לו שאלות." />
        ) : result ? (
          <ResultCard result={result} />
        ) : !attemptId ? (
          <div className="grid gap-3 rounded-md border border-line bg-raised p-4">
            <p className="m-0 text-body">
              {questions.length} שאלות. אחרי כל תשובה רואים מיד אם היא נכונה, ואי אפשר לחזור
              ולשנות.
            </p>
            {quiz.data.time_limit_seconds && (
              <p className="m-0 flex items-center gap-2 text-body-sm text-muted">
                <Clock size={16} strokeWidth={1.75} aria-hidden="true" />
                מוגבל ל {Math.round(quiz.data.time_limit_seconds / 60)} דקות
              </p>
            )}
            <Button
              variant="primary"
              disabled={start.isPending}
              onClick={() => run(() => start.mutateAsync().then(setAttemptId))}
            >
              להתחיל את החידון
            </Button>
          </div>
        ) : (
          current && (
            <>
              <Progress
                value={index}
                max={questions.length}
                label={`שאלה ${index + 1} מתוך ${questions.length}`}
              />

              <article className="grid gap-3 rounded-md border border-line bg-raised p-4">
                <h2 className="m-0 font-display text-display-m font-semibold">{current.prompt}</h2>

                <ul className="m-0 grid list-none gap-2 p-0">
                  {(current.options ?? []).map((option) => {
                    const isPicked = option.id === picked;
                    const isCorrect = feedback?.correct_option_id === option.id;
                    // אחרי המשוב: ירוק על הנכונה, אדום על מה שנבחר בטעות
                    const tone = !feedback
                      ? 'border-line bg-surface'
                      : isCorrect
                        ? 'border-good bg-surface'
                        : isPicked
                          ? 'border-bad bg-surface'
                          : 'border-line bg-surface opacity-60';

                    return (
                      <li key={option.id}>
                        <button
                          type="button"
                          disabled={Boolean(feedback) || answer.isPending}
                          onClick={() => pick(option.id)}
                          className={`flex w-full items-center gap-2 rounded-md border px-3 py-3 text-start text-body ${tone}`}
                        >
                          {feedback && isCorrect && (
                            <Check
                              size={20}
                              strokeWidth={2}
                              aria-hidden="true"
                              className="shrink-0 text-good"
                            />
                          )}
                          {feedback && isPicked && !isCorrect && (
                            <X
                              size={20}
                              strokeWidth={2}
                              aria-hidden="true"
                              className="shrink-0 text-bad"
                            />
                          )}
                          <span className="grow">{option.text}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {feedback && (
                  <div className="grid gap-2">
                    <StatusBadge tone={feedback.correct ? 'good' : 'bad'} className="w-fit">
                      {feedback.correct ? 'נכון' : 'לא נכון'}
                    </StatusBadge>
                    {feedback.explanation && (
                      <p className="m-0 text-body-sm text-muted">{feedback.explanation}</p>
                    )}
                    <Button variant="primary" disabled={finish.isPending} onClick={advance}>
                      {isLast ? 'לסיום ולציון' : 'לשאלה הבאה'}
                      {!isLast && <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" />}
                    </Button>
                  </div>
                )}
              </article>
            </>
          )
        )}
      </div>
    </div>
  );
}

function ResultCard({ result }: { result: QuizResult }) {
  return (
    <div className="grid gap-3 rounded-md border border-line bg-raised p-4">
      <StatusBadge tone={result.passed ? 'good' : 'bad'} className="w-fit">
        {result.passed ? 'עברת' : 'לא עברת הפעם'}
      </StatusBadge>

      <p className="m-0 font-display text-stat font-bold tabular">{result.score}</p>
      <p className="m-0 text-body text-muted">
        {result.correct} תשובות נכונות מתוך {result.total}
      </p>

      <Link
        to="/learning"
        className="inline-flex w-fit items-center gap-2 rounded-sm border border-muted px-6 py-2 text-body font-medium text-ink no-underline"
      >
        חזרה ללומדות
      </Link>
    </div>
  );
}
